/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { type ComponentType } from 'react';

type DefaultComponentModule = { default: ComponentType };

type LazyRouteResult = Promise<{ Component: ComponentType }>;

/**
 * Adapts a dynamic import of a default-exported component into the `{ Component }`
 * shape React Router's route-level `lazy` expects, so a route can code-split with
 * a single line: `lazy: lazyRoute(() => import('...'))`.
 *
 * RR fetches the chunk during the navigation itself, keeping the current page
 * mounted (with the layout's progress bar) instead of flashing a spinner.
 */
export const lazyRoute =
    (load: () => Promise<DefaultComponentModule>) => async (): LazyRouteResult => ({
        Component: (await load()).default,
    });

/**
 * Same as {@link lazyRoute}, but for modules that export a named component
 * instead of `default`. Prefer deep imports over barrel entry points so each
 * page stays in its own chunk (e.g. `site-kit/src/components/...`).
 *
 * @example
 * lazy: lazyNamedRoute(
 *     () => import('site-kit/src/components/AboutUsPage/AboutUsPage'),
 *     'AboutUsPage'
 * )
 */
export const lazyNamedRoute =
    <Name extends string>(
        load: () => Promise<Record<Name, ComponentType>>,
        exportName: Name
    ) =>
    async (): LazyRouteResult => ({
        Component: (await load())[exportName],
    });
